import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  Receipt,
  QrCode,
  Webhook,
  ScrollText,
  LogOut,
  ChevronDown,
  Mail,
  FileDown,
  Menu,
  Settings2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Toaster } from "@/components/ui/sonner";
import { formatEnvironmentLabel } from "@/lib/labels";

const NAV = [
  {
    to: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
    testid: "nav-dashboard",
    module: "hubfinance",
  },
  { to: "/boletos", label: "Boletos", icon: Receipt, testid: "nav-boletos", module: "hubfinance" },
  { to: "/pix-recebidos", label: "Pix Recebidos", icon: QrCode, testid: "nav-pix-recebidos", module: "hubfinance" },
  { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clientes", module: "hubfinance" },
  { to: "/bancos", label: "Bancos", icon: Building2, testid: "nav-bancos", module: "hubfinance" },
  {
    to: "/emitente",
    label: "Emitentes",
    icon: FileText,
    testid: "nav-emitente",
    module: "hubfinance",
  },
  { to: "/regua", label: "Régua e-mail", icon: Mail, testid: "nav-regua", module: "hubfinance" },
  { to: "/cnab", label: "CNAB 240", icon: FileDown, testid: "nav-cnab", module: "hubfinance" },
  { to: "/webhooks", label: "Webhooks", icon: Webhook, testid: "nav-webhooks", module: "hubfinance" },
  { to: "/administracao", label: "Administração", icon: Settings2, testid: "nav-administracao" },
  { to: "/logs", label: "Logs", icon: ScrollText, testid: "nav-logs" },
];

const ENV_BADGE_STYLES = {
  sandbox: "text-orange-700 border-orange-200 bg-orange-50",
  homologacao: "text-amber-700 border-amber-200 bg-amber-50",
  producao: "text-emerald-700 border-emerald-200 bg-emerald-50",
  misto: "text-blue-700 border-blue-200 bg-blue-50",
  sem_banco: "text-zinc-600 border-zinc-200 bg-zinc-50",
};

function EnvironmentBadge() {
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(() => {
    api
      .get("/operacao/ambiente")
      .then(({ data }) => {
        setStatus(data);
      })
      .catch(() => {
        setStatus(null);
      });
  }, []);

  useEffect(() => {
    loadStatus();
    window.addEventListener("up2:bancos-changed", loadStatus);
    return () => {
      window.removeEventListener("up2:bancos-changed", loadStatus);
    };
  }, [loadStatus]);

  const ambiente = status?.ambiente || "sem_banco";
  const label = formatEnvironmentLabel(status?.label || ambiente);
  const className = ENV_BADGE_STYLES[ambiente] || ENV_BADGE_STYLES.sem_banco;
  const title = status?.bancos_ativos
    ? `${status.bancos_ativos} banco(s) ativo(s)`
    : "Nenhum banco ativo configurado";

  return (
    <span
      className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm ${className}`}
      title={title}
      data-testid="environment-badge"
    >
      ● {label}
    </span>
  );
}

function SidebarContent({ user, tenant, logout, pathname, mobile = false }) {
  const modules = tenant?.modules || {};
  const visibleNav = NAV.filter((n) => !n.module || modules[n.module] !== false);

  return (
    <>
      <Link
        to="/"
        className="px-5 py-5 border-b border-zinc-200 flex items-center gap-3"
        data-testid={mobile ? "mobile-sidebar-brand" : "sidebar-brand"}
      >
        <img
          src="icone-logo-up2.png"
          alt="UP2 Soluções"
          className="h-12 w-12 object-contain"
        />
        <div className="leading-tight">
          <div className="font-display font-bold text-sm tracking-tight">
            UP2 FINANCE
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-orange-600 font-mono font-semibold">
            HUB
          </div>
        </div>
      </Link>

      <nav className="p-3 flex-1 space-y-0.5 overflow-y-auto">
        <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
          Operação
        </div>
        {visibleNav.map((n) => {
          const link = (
            <NavLink
              key={n.to}
              to={n.to}
              end
              data-testid={n.testid}
              className="side-link"
              data-active={pathname === n.to}
            >
              <n.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span className="truncate">{n.label}</span>
            </NavLink>
          );

          return mobile ? (
            <SheetClose asChild key={n.to}>
              {link}
            </SheetClose>
          ) : (
            link
          );
        })}
      </nav>

      <div className="p-3 border-t border-zinc-200">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-orange-50 rounded-sm transition-colors"
              data-testid="user-menu-trigger"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F26522] to-[#D14F12] text-white grid place-content-center text-xs font-bold shadow-sm shrink-0">
                {(user?.name || "U").slice(0, 2).toUpperCase()}
              </div>
              <div className="text-left flex-1 min-w-0">
                <div
                  className="text-xs font-semibold truncate"
                  data-testid="user-name"
                >
                  {user?.name}
                </div>
                <div className="text-[10px] text-zinc-500 truncate">
                  {tenant?.empresa || user?.empresa}
                </div>
              </div>
              <ChevronDown className="w-3 h-3 text-zinc-400 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 rounded-sm">
            <DropdownMenuLabel className="text-xs truncate">
              {user?.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer">
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}

export default function AppShell() {
  const { user, tenant, logout } = useAuth();
  const loc = useLocation();
  const modules = tenant?.modules || {};
  const visibleNav = NAV.filter((n) => !n.module || modules[n.module] !== false);
  const current = NAV.find((n) => n.to === loc.pathname) || visibleNav[0] || NAV[0];

  return (
    <div className="min-h-screen flex bg-white" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-zinc-200 bg-white flex-col sticky top-0 h-screen">
        <SidebarContent
          user={user}
          tenant={tenant}
          logout={logout}
          pathname={loc.pathname}
        />
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b border-zinc-200 px-4 sm:px-6 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3 min-w-0">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="lg:hidden h-9 w-9 shrink-0"
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Abrir menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 flex flex-col">
                <SheetTitle className="sr-only">Menu principal</SheetTitle>
                <SidebarContent
                  user={user}
                  tenant={tenant}
                  logout={logout}
                  pathname={loc.pathname}
                  mobile
                />
              </SheetContent>
            </Sheet>
            <div className="hidden sm:block text-[11px] uppercase tracking-[0.2em] text-orange-600 font-mono font-semibold">
              UP2 FINANCE
            </div>
            <span className="hidden sm:block text-zinc-300">/</span>
            <div
              className="font-display font-semibold text-sm truncate"
              data-testid="breadcrumb-current"
            >
              {current.label}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <EnvironmentBadge />
            <span className="hidden md:inline text-xs text-zinc-500 font-mono">
              {tenant?.empresa || user?.empresa}
            </span>
          </div>
        </header>

        <div className="p-6 md:p-8 animate-fade-in-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
